// library imports
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Tooltip,
  Modal,
  Divider,
  Flex,
  Spin,
  Select,
  Popconfirm,
} from "antd";
import { useEffect, useState, useContext } from "react";
// component imports
import TonneProductForm from "./TonneProductForm";
import DataTable from "../../../components/common/DataTable";
// context imports
import { IndexContext } from "../../../context/IndexContext";
// hook imports
import useFetch from "../../../hooks/useFetch";

export default function TonneProductView() {
  // index context
  const {
    siteSelected,
    setSiteSelected,
    siteList,
    getSiteSelectionLoading,
    messageApi,
  } = useContext(IndexContext);

  // fetch wrapper
  const [fetchWrapper] = useFetch();

  // tonne product data to be displayed in table
  const [tonneProductDetails, setTonneProductDetails] = useState([]);

  // tonne product table loading state
  const [tonneProductTableLoading, setTonneProductTableLoading] =
    useState(false);

  // add modal opening state
  const [isAddTonneProductModalOpen, setIsAddTonneProductModalOpen] =
    useState(false);

  // edit modal opening state
  const [isEditTonneProductModalOpen, setIsEditTonneProductModalOpen] =
    useState(false);

  // edit form initial values
  const [formInitialValues, setFormInitialValues] = useState({});

  // year list to be disabled for date picker in tonne product form
  const [disabledYears, setDisabledYears] = useState([]);

  const tonneProductTableColumns = [
    {
      title: "Year",
      dataIndex: "Year",
      key: "Year",
    },
    {
      title: "Products Produced (Tonnes)",
      dataIndex: "TonneProduct",
      key: "TonneProduct",
    },
    {
      title: "",
      dataIndex: "Action",
      key: "Action",
      width: "11%",
      render: (_, record) => (
        <Flex justify="flex-start" align="center" gap={"5px"} wrap={true}>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "blue" }} />}
              onClick={() => {
                getTonneProductFormData(record);
                setIsEditTonneProductModalOpen(true);
              }}
            />
          </Tooltip>

          <Tooltip title="Delete">
            <Popconfirm
              title="Delete Products Produced (Tonnes)"
              description={
                "Are you sure to delete this products produced (tonnes)?"
              }
              onConfirm={() => {
                deleteTonneProduct(record);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                icon={<DeleteOutlined style={{ color: "red" }} />}
              />
            </Popconfirm>
          </Tooltip>
        </Flex>
      ),
    },
  ];

  /* API to get tonne product form data to initialise form when form is in 'edit' mode */
  function getTonneProductFormData(row_record) {
    fetchWrapper({
      method: "GET",
      endpoint_url: "tonne_product_management/getTonneProductFormData",
      set_content_type_header: false,
      params: {
        tonne_product_id: row_record.ID,
      },
      onSuccess: (fetch_output) => {
        setFormInitialValues(fetch_output.data);
      },
    });
  }

  /* API to get tonne product table data */
  function getTonneProductDetails() {
    setTonneProductTableLoading(true);
    fetchWrapper({
      method: "GET",
      endpoint_url: "tonne_product_management/getTonneProductDetails",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        setTonneProductTableLoading(false);
        setTonneProductDetails(fetch_output.data);
      },
    });
  }

  /** API to delete tonne product */
  function deleteTonneProduct(row_record) {
    setTonneProductTableLoading(true);
    fetchWrapper({
      endpoint_url: "tonne_product_management/deleteTonneProduct",
      body: {
        tonne_product_id: row_record.ID,
      },
      onSuccess: (fetch_output) => {
        setTonneProductTableLoading(false);
        if (fetch_output.data === true) {
          messageApi.open({
            type: "success",
            content: fetch_output.message,
          });
          getTonneProductDetails();
          getYearList();
        } else {
          messageApi.open({
            type: "error",
            content: fetch_output.message,
          });
        }
      },
    });
  }

  /* API to get year list of the selected site which the tonne product has been input */
  function getYearList() {
    fetchWrapper({
      method: "GET",
      endpoint_url: "tonne_product_management/getYearList",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        setDisabledYears(fetch_output.data);
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      getTonneProductDetails();
      getYearList();
    }
  }, [siteSelected]);

  return (
    <>
      <Flex align="center">
        <p className="mr-2">Site:</p>
        <Select
          style={{ width: "100%", textAlign: "left" }}
          loading={getSiteSelectionLoading}
          showSearch
          value={siteSelected}
          options={siteList}
          optionFilterProp="label"
          labelRender={(value) => <>{value.label}</>}
          onChange={(value) => {
            setSiteSelected(value);
          }}
        />
      </Flex>

      <Flex>
        <Button
          icon={<PlusOutlined />}
          className="mt-3 mb-5"
          onClick={() => setIsAddTonneProductModalOpen(true)}
          disabled={tonneProductTableLoading}
        >
          Product Produced (Tonne)
        </Button>
      </Flex>

      {tonneProductTableLoading === true ? (
        <Flex justify="center" align="center" style={{ height: "50vh" }}>
          <Spin />
        </Flex>
      ) : (
        <DataTable
          table_column={tonneProductTableColumns}
          table_data={tonneProductDetails}
          scroll_width={1500}
        />
      )}

      <Modal
        title="Add Product Produced (Tonne)"
        open={isAddTonneProductModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <TonneProductForm
          mode="add"
          setIsModalOpen={setIsAddTonneProductModalOpen}
          refreshTable={getTonneProductDetails}
          refreshYearList={getYearList}
          siteSelected={siteSelected}
          disabledYears={disabledYears}
        />
      </Modal>

      <Modal
        title="Edit Product Produced (Tonne)"
        open={isEditTonneProductModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <TonneProductForm
          mode="edit"
          setIsModalOpen={setIsEditTonneProductModalOpen}
          refreshTable={getTonneProductDetails}
          initialValues={formInitialValues}
          disabledYears={disabledYears}
        />
      </Modal>
    </>
  );
}
